/**
 * UpsertOperator Component
 *
 * Description: This component facilitates the creation and updating of operator records, providing form fields
 * for operator details such as name, username, email, role, and password. It manages state for form inputs,
 * password visibility, and form submission. The component fetches available roles from the server, validates
 * the input data, and performs API calls to create or update operators. It provides visual feedback
 * through loaders and success/error messages using toast notifications.
 *
 * Props:
 * - onSuccess (function): Callback function invoked upon successful form submission.
 * - isEdit (IOperatorItems | boolean): Optional. If provided as an object, it represents the operator
 *   to be edited. If `false`, the component operates in add mode.
 *
 * File Name: UpsertOperator.tsx
 * Date: 25-07-2024
 *
 * Marker: DOC_START
 */

import React, { FormEvent, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import SimpleInput from "../SimpleInput";
import httpRequest from "../../helpers/httpRequest";
import { toast } from "react-toastify";
import SelectDropdown from "../GlobalComponents/SelectDropdown";
import PrimaryBtn from "../GlobalComponents/primaryBtn";
import { validatePassword } from "../../helpers/helperFunctions";
import { errMsg, emailRegex, nameRegex } from "../../lib/constans";
import {
  IDropDownArray,
  IOperatorItems,
  IRoleItems,
} from "src/types/GlobalInterfaces";
import CustomWebsiteDropdown from "../GlobalComponents/Widget/CustomWebsiteDropdown";
import { Option } from "react-multi-select-component";

interface IUpsertOperatorProps {
  onSuccess: () => void; // Adjust the type as needed
  isEdit?: IOperatorItems | boolean;
}

const UpsertOperator: React.FC<IUpsertOperatorProps> = ({
  onSuccess = () => {},
  isEdit = false,
}) => {
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] =
    useState<boolean>(false);

  const [getLoader, setGetLoader] = useState<boolean>(false);
  const [allRoles, setAllRoles] = useState<IDropDownArray[]>([]);

  const [role, setRole] = useState<IDropDownArray[]>([]);
  const [userName, setUserName] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [userRoleId, setUserRoleId] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [cnfrmPasword, setCnfrmPasword] = useState<string>("");

  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [error, setError] = useState<{ [key: string]: string }>({});

  // Validation function
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    
    // Name validation
    if (!name.trim()) {
      newErrors.name = "Name is required";
    } else if (!nameRegex.test(name.trim())) {
      newErrors.name = "Name should contain only letters and spaces (2-25 characters)";
    } else if(name.trim().length < 3){
      newErrors.name = "Name should be at least 3 characters";
    } else if(name.trim().length > 30){
      newErrors.name = "Name should be less than 25 characters";
    }
    
    // Username validation
    if (!userName.trim()) {
      newErrors.userName = "Username is required";
    } else if (userName.includes(" ")) {
      newErrors.userName = "Username cannot contain spaces";
    } else if (!/^[a-zA-Z0-9]{2,30}$/.test(userName.trim())) {
      newErrors.userName = "Username should contain only letters and numbers (2-30 characters)";
    }
    
    // Email validation
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }
    
    // Role validation
    if (!userRoleId) {
      newErrors.role = "Role is required";
    }
    
    // Password validation (only for new operators)
    if (!isEdit || password.trim() || cnfrmPasword.trim()) {
      if (!password.trim()) {
        newErrors.password = "Password is required";
      } else if (!validatePassword(password.trim())) {
        newErrors.password = errMsg.invalidPassword;
      }
      
      if (!cnfrmPasword.trim()) {
        newErrors.cnfrmPasword = "Confirm password is required";
      } else if (password.trim() !== cnfrmPasword.trim()) {
        newErrors.cnfrmPasword = "Password and confirm password do not match";
      }
    }

    setError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // API_CALL: API call for creating or editing a operator
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoader(true);
    const path =
      isEdit && typeof isEdit !== "boolean"
        ? `/operators/edit/${isEdit._id}`
        : "/operators/add";
    const params: any = {
      userName: userName.trim(),
      name: name.trim(),
      email: email.trim(),
      userRoleId,
    };

    if (password) {
      params.password = password.trim();
    }

    const { res, err } = await httpRequest<any>({
      method: isEdit ? "put" : "post",
      path,
      params,
    });
    if (res) {
      toast(`Operator ${isEdit ? "updated" : "added"} successfully.`, {
        type: "success",
      });
      onSuccess();
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  const fetchAllRoles = async () => {
    setGetLoader(true);
    const { res, err } = await httpRequest<{ documents: IRoleItems[] }>({
      path: "/roles/all",
    });
    if (res) {
      const { documents } = res;
      setAllRoles(documents.map((v) => ({ label: v.roleName, value: v._id })));
    } else {
      toast(err, { type: "error" });
    }
    setGetLoader(false);
  };

  useEffect(() => {
    fetchAllRoles();
  }, []);

  useEffect(() => {
    if (role.length > 0) {
      setUserRoleId(role[0]?.value || "");
    }
  }, [role]);

  useEffect(() => {
    if (isEdit && typeof isEdit !== "boolean") {
      let { name = "", userName = "", email = "", roleName = "" } = isEdit;
      setName(name);
      setUserName(userName);
      setEmail(email);
      let _obj = allRoles?.find((v) => v?.label === roleName);
      setRole([{ label: _obj?.label || "", value: _obj?.value || "" }]);
    }
  }, [isEdit, allRoles]); // Execute this useEffect hook if in edit mode to initialize form with existing operator data

  return (
    <div className="table-container">
      <div className="admin-create-role-main parent-box-scroll-bar-full">
        <form onSubmit={handleSubmit}>
          <div className="admin-create-role">
            <div className="create-operators-res  create-operators-res">
              {/* name */}
              <SimpleInput
                value={name}
                onChange={(val) => {
                  let filteredValue = val.replace(/[^a-zA-Z\s]/g, "");
                  setName(filteredValue);
                  setError((prev) => ({ ...prev, name: "" }));
                }}
                isLabel={true}
                labelText="Name"
                isRequired={true}
                id="name-input"
                maxLength={30}
                error={error.name}
              />
              {/* user name */}
              <SimpleInput
                value={userName}
                onChange={(val) => {
                  // Remove spaces and special characters, only allow letters and numbers
                  let filteredValue = val.replace(/[^a-zA-Z0-9]/g, "");
                  setUserName(filteredValue);
                  setError((prev) => ({ ...prev, userName: "" }));
                }}
                isLabel={true}
                labelText="User Name"
                isRequired={true}
                id="user-name-input"
                maxLength={30}
                error={error.userName}
              />
              {/* email */}
              <SimpleInput
                type="email"
                value={email}
                onChange={(val) => {
                  // Remove spaces from email
                  let filteredValue = val.replace(/\s/g, "");
                  setEmail(filteredValue);
                  setError((prev) => ({ ...prev, email: "" }));
                }}
                isLabel={true}
                labelText="Email"
                isRequired={true}
                id="email-input"
                maxLength={50}
                error={error.email}
              />
              {/* role */}

              <CustomWebsiteDropdown
                options={allRoles}
                data={role}
                setData={(data) => {
                  setRole(data);
                  setError((prev) => ({ ...prev, role: "" }));
                }}
                labelText="Role"
                isRequired={true}
                placeholder="Select Role"
                dropdownMainId="role-dropdown"
                loading={getLoader}
                clearId="role"
                optionId={(item: Option, index: number) =>
                  `role-option-${index + 1}`
                }
                error={error.role}
              />
              <div className="flex gap-6">
                {/* password */}
                <div className="w-[50%]">
                  <h3 className="font-size-18px font-Poppins-Regular">
                    <span className="text-[#FF0000]">* </span>Password{" "}
                  </h3>
                  <div className="relative operator-pass-eye">
                    <SimpleInput
                      type={passwordVisible ? "text" : "password"}
                      value={password}
                      onChange={(val) => {
                        setPassword(val);
                        setError((prev) => ({ ...prev, password: "" }));
                      }}
                      id="password-input"
                      maxLength={50}
                      error={error.password}
                      showPasswordEye
                      tooglePasswordVisibility={() => setPasswordVisible((prev) => !prev)}
                    />
                  </div>
                </div>
                {/* confirm password */}
                <div className="w-[50%]">
                  <h3 className="font-size-18px font-Poppins-Regular">
                    <span className="text-[#FF0000]">* </span> Confirm Password{" "}
                  </h3>
                  <div className="relative operator-pass-eye">
                    <SimpleInput
                      type={confirmPasswordVisible ? "text" : "password"}
                      value={cnfrmPasword}
                      onChange={(val) => {
                        setCnfrmPasword(val);
                        setError((prev) => ({ ...prev, cnfrmPasword: "" }));
                      }}
                      id="confirm-password-input"
                      maxLength={50}
                      error={error.cnfrmPasword}
                      showPasswordEye
                      tooglePasswordVisibility={() => setConfirmPasswordVisible((prev) => !prev)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end my-6">
            <PrimaryBtn
              ButtonText="Save"
              loader={isLoader}
              type="submit"
              btnExtraStyle={{
                backgroundColor: "#00443f",
                borderRadius: "1.5rem",
              }}
              id="save-operator-btn"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpsertOperator;
